<template>
  <div class="card">

    <div class="bg-blue-light p-1">
      <TitleButton
        :showBtn="true"
        :showAddNew="false"
        title="Update IPD Pharmacy Bill"
        @onClickCloseButton="goToList"
      />
    </div>

    <div class="my-2 px-2">
      <TableHeaderData
        :formData="formData"
        :tableItems="tableItems"
        :additionalProductQuery="additionalProductQuery"
        ref="formHeader"
      />
    </div>

    <div class="col-12 px-2">
      <ListTable
        :tableItems="tableItems"
        @addNewItem="addNewItem"
        :salesAccountHeads="salesAccountHeads"
        :vatRate="vatRate"
        :isHideAddBtn="true"
        :autoFillPayment="false"
      />
    </div>
    <div class="row px-2">
      <div class="col-md-6">
        <div class="mb-1">
          <label class="form-label" for="description">Ledger Memo</label>
          <vField
            as="textarea"
            name="description"
            type="number"
            class="form-control"
            v-model="formData.description"
          />
        </div>
      </div>
      <div class="col-md-5 ml-auto mt-auto mb-1">
        <div class="d-flex flex-wrap gap-1 gy-2">
          <button :disabled="submitLoading" @click="handleSubmit(false)" class="btn btn-primary">Update</button>
          <button :disabled="submitLoading" @click="handleSubmit(true)" class="btn btn-primary">Update & Print</button>
        </div>
      </div>
    </div>

    <Loader v-if="loader"/>
  </div>
</template>

<script setup>
import {computed, inject, onMounted, provide, ref} from 'vue';
import TitleButton from '@/components/atom/TitleButton';
import ListTable from '@/components/molecule/company/hospital/general-billing/v2/InvoiceHospitalTable.vue';
import TableHeaderData from '@/components/atom/hospital/ipd-pharmacy-bill/TableHeaderIpdPharmacy.vue';
import {useRoute, useRouter} from 'vue-router';
import handlePurchase from '@/services/modules/purchase';
import {vatRate} from '@/data/inventory.js';
import handleCompany from "@/services/modules/company";
import hospitalPdfPrinterHelper from '@/services/utils/hospitalPdfPrinterHelper';
import useDate from "@/services/utils/day";
import Loader from '@/components/atom/LoaderComponent'

const {generatePdf} = hospitalPdfPrinterHelper();
const {fetchSalesBill, ...rest} = handlePurchase();
const {fetchCompanyInfo, fetchActiveCustomizationField} = handleCompany();
const $route = useRoute();
const $router = useRouter();
const date = useDate();
const customizationPdfFields = ref([]);

const companyId = $route.params.companyId;
const loader = ref(true);
const submitLoading = ref(false);
const formHeader = ref(null);
const salesAccountHeads = ref([]);
const totals = ref({});
const invoiceRes = ref({});
const company = ref({});
const ipdRegisterId = computed(() => $route.params.ipdRegisterId || $route.query.ipd_register_id);
let formData = ref({
  company_id: companyId,
  contact_profile_id: null,
  referrer_id: null,
  account_head_id: null,
  ipd_register_id: ipdRegisterId.value,
  vat_payable_account_head_id: null,
  project_id: null,
  cost_centre_id: null,
  location_id: null,
  mop_account_head_id: null,
  discount_head_id: null,
  service_resource_id: null,
  receipt_reference: null,
  adjustment_amount: null,
  receipt_description: "IPD pharmacy bill payment",
  payment: null,
  status: 'active',
  bill_number: '',
  money_receipt_no: '',
  date: '',
  has_item_detail: true,
  description: '',
  order_number: null,
  sales_person_id: null,
  commission_percent: null,
  lc_number: null,
  item_details: []
});

provide('formData', formData);
provide('totals', totals);
const showSuccess = inject('showSuccess');
const showError = inject('showError');

let tableItems = ref([]);

const additionalProductQuery = computed(() => {
  return {
    location_id: formData.value.location_id,
    with_desc_stock: 1
  }
})

const getActiveFiledCustomization = async () => {
  const companyQuery = `?company_id=${companyId}&customization_type=pdf&type=pharmacy_bill_pdf`;
  const res = await fetchActiveCustomizationField(companyQuery);
  customizationPdfFields.value = res.data;
};

const loadBill = async () => {
  const companyQuery = `?company_id=${companyId}`;
  await fetchSalesBill($route.params.ipdId, companyQuery).then(res => {
    if (!res.data) return;
    const {receipt_generals} = res.data;
    formData.value = {
      company_id: res.data.company_id,
      contact_profile_id: res.data.contact_profile_id,
      account_head_id: res.data.account_head_id,
      ipd_register_id: res.data.ipd_register_id,
      referrer_id: res.data.referrer_id,
      vat_payable_account_head_id: res.data.vat_payable_account_head_id,
      receipt_description: "IPD pharmacy bill payment",
      project_id: res.data.project_id,
      cost_centre_id: res.data.cost_centre_id,
      location_id: res.data.location_id,
      mop_account_head_id: res.data.mop_account_head_id,
      discount_head_id: res.data.discount_head_id,
      adjustment_amount: res.data.round_up_discount,
      status: 'active',
      bill_number: res.data.bill_number,
      date: res.data.date,
      has_item_detail: res.data.has_item_detail,
      description: res.data.description,
      payment: null,
      item_details: res.data.general,
    };
    tableItems.value = res.data.general.map(({product, ...item}) => {
      return {
        ...item,
        name: product.name,
        product: {
          name: product.name
        }
      }
    });
    if (receipt_generals && receipt_generals[0]) {
      formData.value.money_receipt_no = receipt_generals[0].receipt_master.voucher_no;
      formData.value.mop_account_head_id = receipt_generals[0].receipt_master.account_head_id;
      formData.value.receipt_master_id = receipt_generals[0].receipt_master.id;
      formData.value.receipt_reference = receipt_generals[0].receipt_master.receipt_reference;
      formData.value.payment = receipt_generals[0].receipt_master.total_paid_amount;
    }
  })
}

onMounted(() => {
  loader.value = true;
  const companyQuery = `?company_id=${companyId}`;
  Promise.all([
    rest.fetchAccountReceivable(companyQuery).then(res => {
      if (res.data) formData.value.account_head_id = res.data[0].id;
    }),
    rest.fetchVatPayable(companyQuery).then(res => {
      if (res.data) formData.value.vat_payable_account_head_id = res.data[0].id;
    }),
    fetchCompanyInfo(companyId).then(res => {
      if (res.data) company.value = res.data;
    }),
    rest.getAccountHeadBySlag("discount_account", companyQuery).then(res => {
      if (res.data) salesAccountHeads.value = res.data;
    }),
    getActiveFiledCustomization(),
    loadBill(),
  ])
    .finally(() => {
      loader.value = false
    })
})

const addNewItem = () => {
  tableItems.value.push({
    id: null,
    product_id: null,
    name: '',
    description: '',
    quantity: 1,
    rate: 0,
    purchase_rate: 0,
    discount_percent: 0,
    vat: 0,
    sub_total: 0,
    discount_amount: 0,
    vat_amount: 0,
    total: 0,
    is_edit: false,
  });
}

const goToList = () => {
  const backUrl = $route.query.back_url ? decodeURIComponent($route.query.back_url) : null;
  if (backUrl) {
    $router.push(backUrl);
    return;
  }

  $router.back();
}

const handleSubmit = (print = false) => {
  submitLoading.value = true;
  if (!formData.value.date) formData.value.date = date.currentDate();
  formData.value.item_details = tableItems;
  let copyFormData = Object.assign({}, formData.value);
  copyFormData.item_details = formData.value.item_details;
  copyFormData.sale_type = "ipd_pharmacy_invoice";
  copyFormData.receipt_type = "ipd_pharmacy_invoice";

  rest.updateSalesBill($route.params.ipdId, copyFormData)
    .then(res => {
      submitLoading.value = false;
      if (!res.status) return showError(res.message);
      showSuccess(res.message);
      if (print) goToPrint(res.data.id);
      goToList();
    })
    .catch(err => {
      submitLoading.value = false;
      showError(err.response.data.message);
    })
}

const goToPrint = (id) => {
  const companyQuery = `?company_id=${companyId}`;
  fetchSalesBill(id, companyQuery).then((res) => {
    if (res) invoiceRes.value = res.data;
  }).then(() => {
    generatePdf(
      company.value,
      invoiceRes.value,
      'BILL',
      true,
      true,
      false,
      false,
      customizationPdfFields.value && customizationPdfFields.value.heading_left_pos_print,
      customizationPdfFields.value
    )
  }).catch((err) => {
  })
}
</script>
<style scoped>
address {
  margin-bottom: 0 !important;
}
</style>
